import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { landingAPI, formAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowLeft, Save, Eye, Globe, Search, FileText, 
  Plus, Trash2, GripVertical, Copy, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

// Default sections for a landing page
const DEFAULT_SECTIONS = [
  {
    id: 'hero',
    type: 'hero',
    title: '',
    subtitle: '',
    cta_text: '',
    cta_link: '#contact',
    background_image: '',
  },
  {
    id: 'services',
    type: 'services',
    title: '',
    items: [],
  },
  {
    id: 'about',
    type: 'about',
    title: '',
    content: '',
    image: '',
  },
  {
    id: 'contact',
    type: 'contact',
    title: '',
    subtitle: '',
    form_id: '',
  },
];

export function LandingPageBuilderPage() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const { workspace } = useAuth();
  const { t, language } = useLanguage();
  const isEditing = !!pageId;
  
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [forms, setForms] = useState([]);
  
  // Page data
  const [service, setService] = useState('');
  const [city, setCity] = useState('');
  const [template, setTemplate] = useState('classic');
  const [isPublished, setIsPublished] = useState(false);
  
  // SEO
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [slug, setSlug] = useState('');
  
  // Content sections
  const [sections, setSections] = useState([]);
  
  // Schema LocalBusiness
  const [schemaEnabled, setSchemaEnabled] = useState(true);

  useEffect(() => {
    loadForms();
    if (isEditing) {
      loadPage();
    } else {
      initializeNewPage();
    }
  }, [pageId]);

  const loadForms = async () => {
    try {
      const response = await formAPI.list();
      setForms(response.data);
    } catch (err) {
      console.error('Error loading forms:', err);
    }
  };

  const loadPage = async () => {
    try {
      const response = await landingAPI.get(pageId);
      const page = response.data;
      
      setService(page.service || '');
      setCity(page.city || '');
      setTemplate(page.template || 'classic');
      setIsPublished(page.is_published || false);
      setSeoTitle(page.seo?.title || '');
      setSeoDescription(page.seo?.meta_description || '');
      setSlug(page.slug || '');
      setSections(page.sections || []);
      setSchemaEnabled(page.schema_enabled !== false);
    } catch (err) {
      console.error('Error loading page:', err);
      toast.error(t('landingPages.pageNotFound') || 'Page not found');
      navigate('/landing-pages');
    } finally {
      setIsLoading(false);
    }
  };

  const initializeNewPage = () => {
    // Set defaults from workspace
    const defaultService = workspace?.services?.[0] || '';
    const defaultCity = workspace?.cities?.[0] || '';
    
    setService(defaultService);
    setCity(defaultCity);
    
    // Generate default slug
    updateSlug(defaultService, defaultCity);
    
    // Initialize sections with translated content
    const heroTitle = language === 'fr' 
      ? `${defaultService} à ${defaultCity}` 
      : `${defaultService} in ${defaultCity}`;
    const heroSubtitle = language === 'fr'
      ? 'Service professionnel, rapide et fiable'
      : 'Professional, fast and reliable service';
    const ctaText = language === 'fr' ? 'Demander une soumission' : 'Request a Quote';
    const servicesTitle = language === 'fr' ? 'Nos services' : 'Our Services';
    const aboutTitle = language === 'fr' ? 'Pourquoi nous choisir?' : 'Why Choose Us?';
    const contactTitle = language === 'fr' ? 'Contactez-nous' : 'Contact Us';
    const contactSubtitle = language === 'fr' 
      ? 'Obtenez une soumission gratuite en quelques minutes'
      : 'Get a free quote in minutes';
    
    setSections([
      {
        id: 'hero',
        type: 'hero',
        title: heroTitle,
        subtitle: heroSubtitle,
        cta_text: ctaText,
        cta_link: '#contact',
      },
      {
        id: 'services',
        type: 'services',
        title: servicesTitle,
        items: workspace?.services?.slice(0, 6).map(s => ({
          title: s,
          description: '',
          icon: 'check',
        })) || [],
      },
      {
        id: 'about',
        type: 'about',
        title: aboutTitle,
        content: language === 'fr'
          ? `Avec plus de 10 ans d'expérience, nous offrons des services de ${defaultService.toLowerCase()} de qualité supérieure à ${defaultCity} et ses environs.`
          : `With over 10 years of experience, we offer superior quality ${defaultService.toLowerCase()} services in ${defaultCity} and surrounding areas.`,
      },
      {
        id: 'contact',
        type: 'contact',
        title: contactTitle,
        subtitle: contactSubtitle,
        form_id: forms[0]?.form_id || '',
      },
    ]);
    
    // Generate SEO
    generateSEO(defaultService, defaultCity);
  };

  const updateSlug = (serviceVal, cityVal) => {
    const slugify = (str) => str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    
    const newSlug = `${slugify(serviceVal)}-${slugify(cityVal)}`;
    setSlug(newSlug);
  };

  const generateSEO = (serviceVal, cityVal) => {
    if (language === 'fr') {
      setSeoTitle(`${serviceVal} à ${cityVal} | ${workspace?.name || 'SpaceLeadX'}`);
      setSeoDescription(`Service professionnel de ${serviceVal.toLowerCase()} à ${cityVal}. Soumission gratuite, travail garanti. Contactez-nous dès aujourd'hui!`);
    } else {
      setSeoTitle(`${serviceVal} in ${cityVal} | ${workspace?.name || 'SpaceLeadX'}`);
      setSeoDescription(`Professional ${serviceVal.toLowerCase()} service in ${cityVal}. Free quotes, guaranteed work. Contact us today!`);
    }
  };

  const handleServiceChange = (newService) => {
    setService(newService);
    updateSlug(newService, city);
    generateSEO(newService, city);
    
    // Update hero section
    setSections(prev => prev.map(s => {
      if (s.type === 'hero') {
        return {
          ...s,
          title: language === 'fr' 
            ? `${newService} à ${city}` 
            : `${newService} in ${city}`,
        };
      }
      return s;
    }));
  };

  const handleCityChange = (newCity) => {
    setCity(newCity);
    updateSlug(service, newCity);
    generateSEO(service, newCity);
    
    // Update hero section
    setSections(prev => prev.map(s => {
      if (s.type === 'hero') {
        return {
          ...s,
          title: language === 'fr' 
            ? `${service} à ${newCity}` 
            : `${service} in ${newCity}`,
        };
      }
      return s;
    }));
  };

  const updateSection = (sectionId, updates) => {
    setSections(prev => prev.map(s => 
      s.id === sectionId ? { ...s, ...updates } : s
    ));
  };

  const handleSave = async () => {
    if (!service.trim() || !city.trim()) {
      toast.error(language === 'fr' ? 'Service et ville requis' : 'Service and city required');
      return;
    }

    setIsSaving(true);
    
    try {
      const pageData = {
        service: service.trim(),
        city: city.trim(),
        template,
        slug,
        is_published: isPublished,
        seo: {
          title: seoTitle,
          meta_description: seoDescription,
        },
        sections,
        schema_enabled: schemaEnabled,
      };

      if (isEditing) {
        await landingAPI.update(pageId, pageData);
        toast.success(language === 'fr' ? 'Page mise à jour' : 'Page updated');
      } else {
        const response = await landingAPI.create(pageData);
        toast.success(language === 'fr' ? 'Page créée' : 'Page created');
        navigate(`/landing-pages/${response.data.page_id}`);
      }
    } catch (err) {
      console.error('Error saving page:', err);
      const message = err.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Error saving page');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicate = async () => {
    if (!isEditing) return;
    
    try {
      const newCity = prompt(language === 'fr' ? 'Nouvelle ville:' : 'New city:', city);
      if (!newCity) return;
      
      // Helper function to replace city in string values
      const replaceCity = (value) => {
        if (typeof value === 'string') {
          return value.replace(new RegExp(city, 'gi'), newCity);
        }
        return value;
      };
      
      // Helper function to process section content (handles both flat and nested formats)
      const processSection = (section) => {
        const newSection = { ...section };
        
        // Handle flat format (title, subtitle, content as direct properties)
        if (typeof newSection.title === 'string') {
          newSection.title = replaceCity(newSection.title);
        }
        if (typeof newSection.subtitle === 'string') {
          newSection.subtitle = replaceCity(newSection.subtitle);
        }
        if (typeof newSection.content === 'string') {
          newSection.content = replaceCity(newSection.content);
        }
        
        // Handle nested format (content as object with title, subtitle, etc.)
        if (newSection.content && typeof newSection.content === 'object') {
          newSection.content = { ...newSection.content };
          if (typeof newSection.content.title === 'string') {
            newSection.content.title = replaceCity(newSection.content.title);
          }
          if (typeof newSection.content.subtitle === 'string') {
            newSection.content.subtitle = replaceCity(newSection.content.subtitle);
          }
        }
        
        return newSection;
      };
      
      const pageData = {
        service,
        city: newCity,
        template,
        slug: `${slug.split('-').slice(0, -1).join('-')}-${newCity.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
        is_published: false,
        seo: {
          title: replaceCity(seoTitle),
          meta_description: replaceCity(seoDescription),
        },
        sections: sections.map(processSection),
        schema_enabled: schemaEnabled,
      };

      const response = await landingAPI.create(pageData);
      toast.success(language === 'fr' ? 'Page dupliquée!' : 'Page duplicated!');
      navigate(`/landing-pages/${response.data.page_id}`);
    } catch (err) {
      console.error('Error duplicating:', err);
      toast.error(err.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    }
  };

  const getPreviewUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/page/${workspace?.slug || 'preview'}/${slug}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="landing-page-builder">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/landing-pages')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-black tracking-tight">
              {isEditing 
                ? (language === 'fr' ? 'Modifier la page' : 'Edit Page')
                : (language === 'fr' ? 'Nouvelle page' : 'New Page')
              }
            </h1>
            {slug && (
              <p className="text-muted-foreground text-sm mt-1">
                /{slug}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-3">
          {isEditing && (
            <>
              <Button variant="outline" onClick={handleDuplicate} data-testid="duplicate-btn">
                <Copy className="mr-2 h-4 w-4" />
                {language === 'fr' ? 'Dupliquer' : 'Duplicate'}
              </Button>
              <Button variant="outline" asChild>
                <a href={getPreviewUrl()} target="_blank" rel="noopener noreferrer">
                  <Eye className="mr-2 h-4 w-4" />
                  {language === 'fr' ? 'Aperçu' : 'Preview'}
                </a>
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => navigate('/landing-pages')}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isSaving} data-testid="save-page-btn">
            <Save className="mr-2 h-4 w-4" />
            {isSaving 
              ? (language === 'fr' ? 'Enregistrement...' : 'Saving...') 
              : t('common.save')
            }
          </Button>
        </div>
      </div>

      <Tabs defaultValue="content" className="space-y-6">
        <TabsList>
          <TabsTrigger value="content">
            <FileText className="h-4 w-4 mr-2" />
            {language === 'fr' ? 'Contenu' : 'Content'}
          </TabsTrigger>
          <TabsTrigger value="seo">
            <Search className="h-4 w-4 mr-2" />
            SEO
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Globe className="h-4 w-4 mr-2" />
            {language === 'fr' ? 'Paramètres' : 'Settings'}
          </TabsTrigger>
        </TabsList>

        {/* Content Tab */}
        <TabsContent value="content" className="space-y-6">
          {/* Service & City Selection */}
          <Card>
            <CardHeader>
              <CardTitle>{language === 'fr' ? 'Service et Localisation' : 'Service & Location'}</CardTitle>
              <CardDescription>
                {language === 'fr' 
                  ? 'Choisissez le service et la ville pour cette page'
                  : 'Choose the service and city for this page'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Service' : 'Service'} *</Label>
                <Select value={service} onValueChange={handleServiceChange}>
                  <SelectTrigger data-testid="service-select">
                    <SelectValue placeholder={language === 'fr' ? 'Sélectionner un service' : 'Select a service'} />
                  </SelectTrigger>
                  <SelectContent>
                    {workspace?.services?.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Ville' : 'City'} *</Label>
                <Select value={city} onValueChange={handleCityChange}>
                  <SelectTrigger data-testid="city-select">
                    <SelectValue placeholder={language === 'fr' ? 'Sélectionner une ville' : 'Select a city'} />
                  </SelectTrigger>
                  <SelectContent>
                    {workspace?.cities?.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Sections Editor */}
          <div className="space-y-4">
            {sections.map((section, index) => (
              <Card key={section.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg capitalize">
                      {section.type === 'hero' && (language === 'fr' ? 'En-tête (Hero)' : 'Hero Section')}
                      {section.type === 'services' && (language === 'fr' ? 'Services' : 'Services')}
                      {section.type === 'about' && (language === 'fr' ? 'À propos' : 'About')}
                      {section.type === 'contact' && (language === 'fr' ? 'Contact' : 'Contact')}
                    </CardTitle>
                    <Badge variant="secondary">{section.type}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Hero Section */}
                  {section.type === 'hero' && (
                    <>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Titre principal (H1)' : 'Main Title (H1)'}</Label>
                        <Input
                          value={section.title}
                          onChange={(e) => updateSection(section.id, { title: e.target.value })}
                          placeholder={language === 'fr' ? 'Ex: Réparation de pavé uni à Longueuil' : 'Ex: Paving Repair in Montreal'}
                          data-testid="hero-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Sous-titre' : 'Subtitle'}</Label>
                        <Input
                          value={section.subtitle}
                          onChange={(e) => updateSection(section.id, { subtitle: e.target.value })}
                          placeholder={language === 'fr' ? 'Service professionnel...' : 'Professional service...'}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Texte du bouton (CTA)' : 'Button Text (CTA)'}</Label>
                          <Input
                            value={section.cta_text}
                            onChange={(e) => updateSection(section.id, { cta_text: e.target.value })}
                            placeholder={language === 'fr' ? 'Demander une soumission' : 'Request a Quote'}
                            data-testid="cta-text-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Lien du bouton' : 'Button Link'}</Label>
                          <Input
                            value={section.cta_link}
                            onChange={(e) => updateSection(section.id, { cta_link: e.target.value })}
                            placeholder="#contact"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Services Section */}
                  {section.type === 'services' && (
                    <>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Titre de la section' : 'Section Title'}</Label>
                        <Input
                          value={section.title}
                          onChange={(e) => updateSection(section.id, { title: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Services affichés' : 'Displayed Services'}</Label>
                        <div className="flex flex-wrap gap-2">
                          {section.items?.map((item, i) => (
                            <Badge key={i} variant="secondary" className="px-3 py-1">
                              {item.title}
                            </Badge>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {language === 'fr' 
                            ? 'Les services sont chargés depuis vos paramètres'
                            : 'Services are loaded from your settings'
                          }
                        </p>
                      </div>
                    </>
                  )}

                  {/* About Section */}
                  {section.type === 'about' && (
                    <>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Titre' : 'Title'}</Label>
                        <Input
                          value={section.title}
                          onChange={(e) => updateSection(section.id, { title: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Contenu' : 'Content'}</Label>
                        <Textarea
                          value={section.content}
                          onChange={(e) => updateSection(section.id, { content: e.target.value })}
                          rows={4}
                          placeholder={language === 'fr' ? 'Décrivez votre entreprise...' : 'Describe your business...'}
                        />
                      </div>
                    </>
                  )}

                  {/* Contact Section */}
                  {section.type === 'contact' && (
                    <>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Titre' : 'Title'}</Label>
                        <Input
                          value={section.title}
                          onChange={(e) => updateSection(section.id, { title: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Sous-titre' : 'Subtitle'}</Label>
                        <Input
                          value={section.subtitle}
                          onChange={(e) => updateSection(section.id, { subtitle: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Formulaire lié' : 'Linked Form'}</Label>
                        <Select 
                          value={section.form_id} 
                          onValueChange={(v) => updateSection(section.id, { form_id: v })}
                        >
                          <SelectTrigger data-testid="form-select">
                            <SelectValue placeholder={language === 'fr' ? 'Sélectionner un formulaire' : 'Select a form'} />
                          </SelectTrigger>
                          <SelectContent>
                            {forms.map((form) => (
                              <SelectItem key={form.form_id} value={form.form_id}>
                                {form.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {forms.length === 0 && (
                          <p className="text-xs text-muted-foreground">
                            {language === 'fr' 
                              ? 'Aucun formulaire. Créez-en un d\'abord.'
                              : 'No forms. Create one first.'
                            }
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* SEO Tab */}
        <TabsContent value="seo" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>
                {language === 'fr' ? 'Optimisation SEO' : 'SEO Optimization'}
              </CardTitle>
              <CardDescription>
                {language === 'fr' 
                  ? 'Configurez les balises meta pour les moteurs de recherche'
                  : 'Configure meta tags for search engines'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'URL (slug)' : 'URL (slug)'}</Label>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">/</span>
                  <Input
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                    placeholder="service-ville"
                    data-testid="slug-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Titre SEO (title tag)' : 'SEO Title (title tag)'}</Label>
                <Input
                  value={seoTitle}
                  onChange={(e) => setSeoTitle(e.target.value)}
                  placeholder={language === 'fr' ? 'Titre pour Google...' : 'Title for Google...'}
                  data-testid="seo-title-input"
                />
                <p className="text-xs text-muted-foreground">
                  {seoTitle.length}/60 {language === 'fr' ? 'caractères' : 'characters'}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Meta description' : 'Meta Description'}</Label>
                <Textarea
                  value={seoDescription}
                  onChange={(e) => setSeoDescription(e.target.value)}
                  placeholder={language === 'fr' ? 'Description pour Google...' : 'Description for Google...'}
                  rows={3}
                  data-testid="seo-description-input"
                />
                <p className="text-xs text-muted-foreground">
                  {seoDescription.length}/160 {language === 'fr' ? 'caractères' : 'characters'}
                </p>
              </div>

              <Separator />

              {/* Google Preview */}
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Aperçu Google' : 'Google Preview'}</Label>
                <div className="p-4 border rounded-lg bg-white">
                  <p className="text-blue-600 text-lg hover:underline cursor-pointer truncate">
                    {seoTitle || 'Page Title'}
                  </p>
                  <p className="text-green-700 text-sm truncate">
                    {getPreviewUrl()}
                  </p>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    {seoDescription || 'Meta description will appear here...'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Schema LocalBusiness */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Schema LocalBusiness</CardTitle>
                  <CardDescription>
                    {language === 'fr' 
                      ? 'Données structurées pour les résultats enrichis Google'
                      : 'Structured data for Google rich results'
                    }
                  </CardDescription>
                </div>
                <Switch
                  checked={schemaEnabled}
                  onCheckedChange={setSchemaEnabled}
                />
              </div>
            </CardHeader>
            {schemaEnabled && (
              <CardContent>
                <div className="p-3 bg-muted/50 rounded-lg text-sm font-mono text-muted-foreground">
                  {`{
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "${workspace?.name || 'Business Name'}",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "${city}"
  },
  "areaServed": "${city}",
  "description": "${seoDescription.slice(0, 100)}..."
}`}
                </div>
              </CardContent>
            )}
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{language === 'fr' ? 'Publication' : 'Publishing'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>{language === 'fr' ? 'Publier la page' : 'Publish Page'}</Label>
                  <p className="text-sm text-muted-foreground">
                    {language === 'fr' 
                      ? 'Rendre la page accessible au public'
                      : 'Make the page publicly accessible'
                    }
                  </p>
                </div>
                <Switch
                  checked={isPublished}
                  onCheckedChange={setIsPublished}
                  data-testid="publish-switch"
                />
              </div>
              
              {isPublished && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">
                    {language === 'fr' ? 'Page publiée' : 'Page Published'}
                  </p>
                  <a 
                    href={getPreviewUrl()} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-green-600 hover:underline flex items-center gap-1 mt-1"
                  >
                    {getPreviewUrl()}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}

              <Separator />

              <div className="space-y-2">
                <Label>Template</Label>
                <Select value={template} onValueChange={setTemplate}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="classic">Classic</SelectItem>
                    <SelectItem value="modern">Modern</SelectItem>
                    <SelectItem value="minimal">Minimal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
